import { CodeBlock } from "@/components/ui/codeblock";
import { useHighlightedText } from "@/utils/searchUtils";
import React, {
  FC,
  JSXElementConstructor,
  useContext,
  memo,
  createContext,
  ComponentProps,
} from "react";
import ReactMarkdown, { Options } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

const searchQueryContext = createContext<string | undefined>(undefined);

// Memoized component to highlight search term matches in text
const HighlightedText = memo(
  ({ text, searchQuery }: { text: string; searchQuery?: string }) => {
    // Use our custom hook to get highlighted parts
    const parts = useHighlightedText(text, searchQuery);

    if (!searchQuery || !text) return text;

    return (
      <>
        {parts.map((part, i) => {
          if (part.isMatch) {
            return (
              <span
                key={i}
                className="bg-primary-200 text-primary-foreground font-semibold px-1 mx-0.5 rounded-sm"
              >
                {part.text}
              </span>
            );
          }
          return part.text;
        })}
      </>
    );
  },
);

const WithSearchHighlight = memo(
  ({
    children,
    className,
    componentType,
  }: {
    children: React.ReactNode;
    className?: string;
    componentType?: keyof JSX.IntrinsicElements | JSXElementConstructor<any>;
  }) => {
    const searchQuery = useContext(searchQueryContext);

    const ComponentType = componentType || "p";

    // Apply highlighting to string content in paragraphs
    if (searchQuery) {
      if (typeof children === "string") {
        return (
          <ComponentType className={className}>
            {<HighlightedText text={children} searchQuery={searchQuery} />}
          </ComponentType>
        );
      } else if (Array.isArray(children)) {
        // Handle array of children (common in Markdown parsing)
        return (
          <ComponentType className={className}>
            {children.map((child, i) => {
              if (typeof child === "string") {
                return (
                  <HighlightedText
                    key={i}
                    text={child}
                    searchQuery={searchQuery}
                  />
                );
              }
              return child;
            })}
          </ComponentType>
        );
      }
    }
    return <ComponentType className={className}>{children}</ComponentType>;
  },
);

export const SearchQueryProvider = memo(
  ({
    children,
    searchQuery,
  }: {
    children: React.ReactNode;
    searchQuery: string | undefined;
  }) => {
    return (
      <searchQueryContext.Provider value={searchQuery}>
        {children}
      </searchQueryContext.Provider>
    );
  },
);

const components: ComponentProps<typeof ReactMarkdown>["components"] = {
  a({ children }) {
    return (
      <a
        href={children?.toString() || ""}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400"
      >
        {children}
      </a>
    );
  },
  p({ children }) {
    return (
      <WithSearchHighlight className="mb-1">{children}</WithSearchHighlight>
    );
  },
  li({ children }) {
    return (
      <WithSearchHighlight className="mb-1" componentType="li">
        {children}
      </WithSearchHighlight>
    );
  },
  ul({ children, node }) {
    return <ul className="list-disc pl-4">{children}</ul>;
  },
  ol({ children, node }) {
    return <ol className="list-decimal pl-4 mb-2">{children}</ol>;
  },
  // @ts-ignore
  code({ node, inline, className, children, ...props }) {
    if (children.length) {
      if (children[0] == "▍") {
        return <span className="mt-1 cursor-default animate-pulse">▍</span>;
      }

      children[0] = (children[0] as string).replace("`▍`", "▍");
    }

    const match = /language-(\w+)/.exec(className || "");

    if (inline) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }

    const codeContent = String(children).replace(/\n$/, "");
    const codeKey = `${node.position?.start.offset}`;

    return (
      <CodeBlock
        key={codeKey}
        language={(match && match[1]) || ""}
        value={codeContent}
        {...props}
      />
    );
  },
};

const remarkPlugins = [remarkGfm, remarkMath];

export const ConfiguredReactMarkdown: FC<Options> = memo(
  (props) => (
    <ReactMarkdown
      remarkPlugins={remarkPlugins}
      components={components}
      {...props}
    />
  ),
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className &&
    prevProps.components === nextProps.components,
);
